<template>
  <div class="shipping-weather-tracker">
    <!-- Header -->
    <div class="tracker-header d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-2">
        <div class="header-icon">
          <i class="bi bi-truck"></i>
        </div>
        <div>
          <h4 class="mb-0">Shipping Weather Alert</h4>
          <small class="text-muted">Temperature-sensitive freight protection</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <div class="btn-group btn-group-sm">
          <button 
            class="btn" 
            :class="unitGroup === 'metric' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setUnitGroup('metric')"
          >
            °C
          </button>
          <button 
            class="btn" 
            :class="unitGroup === 'us' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setUnitGroup('us')"
          >
            °F
          </button>
        </div>
      </div>
    </div>

    <!-- Sign-in Required -->
    <div v-if="!hasSession" class="alert alert-warning d-flex align-items-center">
      <i class="bi bi-lock me-3 fs-4"></i>
      <div class="flex-grow-1">
        <strong>Sign in required</strong>
        <p class="mb-0 small">Please sign in to access weather data for shipping alerts.</p>
      </div>
      <a class="btn btn-primary btn-sm" href="#" onclick="window.dialogManager?.showSignIn(); return false;">
        <i class="bi bi-box-arrow-in-right me-1"></i>Sign In
      </a>
    </div>

    <template v-else>
      <!-- Quick Preset Buttons -->
      <div class="preset-bar bg-light rounded p-3 mb-4">
        <div class="d-flex align-items-center flex-wrap gap-2">
          <span class="text-muted small me-2">Quick Presets:</span>
          <button 
            v-for="preset in presets"
            :key="preset.id"
            class="btn btn-sm"
            :class="activePreset === preset.id ? 'btn-primary' : 'btn-outline-secondary'"
            @click="applyPreset(preset)"
          >
            <i :class="preset.icon" class="me-1"></i>
            {{ preset.name }}
          </button>
        </div>
      </div>

      <div class="row g-4">
        <!-- Left Column: Setup -->
        <div class="col-lg-4">
          <!-- Shipping Stops Panel -->
          <div class="card mb-3">
            <div class="card-header py-2">
              <h6 class="mb-0">
                <i class="bi bi-geo-alt-fill me-2"></i>Stops / Destinations
              </h6>
            </div>
            <div class="card-body">
              <ExceptionLocationPanel 
                v-model="locations"
                label=""
                itemLabel="stop"
                icon="bi bi-pin-map"
                placeholder="City, address, or coordinates..."
                maxHeight="180px"
              />
            </div>
          </div>

          <!-- Shipping Window -->
          <div class="card mb-3">
            <div class="card-header py-2">
              <h6 class="mb-0">
                <i class="bi bi-calendar-week me-2"></i>Shipping Window
              </h6>
            </div>
            <div class="card-body">
              <div class="btn-group btn-group-sm w-100 mb-2">
                <button 
                  class="btn"
                  :class="startPeriod === 'next3days' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setDateRange('next3days')"
                >
                  3 Days
                </button>
                <button 
                  class="btn"
                  :class="startPeriod === 'next5days' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setDateRange('next5days')"
                >
                  5 Days
                </button>
                <button 
                  class="btn"
                  :class="startPeriod === 'next7days' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setDateRange('next7days')"
                >
                  7 Days
                </button>
              </div>
              <small class="text-muted">
                <i class="bi bi-info-circle me-1"></i>
                Checking temperatures for next {{ startPeriod === 'next3days' ? '3' : startPeriod === 'next5days' ? '5' : '7' }} days
              </small>
            </div>
          </div>

          <!-- Temperature Thresholds -->
          <div class="card mb-3">
            <div class="card-header py-2">
              <h6 class="mb-0">
                <i class="bi bi-thermometer-half me-2"></i>Temperature Limits
              </h6>
            </div>
            <div class="card-body">
              <!-- Cold Threshold -->
              <div class="threshold-section mb-4">
                <div class="d-flex align-items-center mb-2">
                  <i class="bi bi-thermometer-snow text-info fs-5 me-2"></i>
                  <span class="fw-medium">Cold Threshold</span>
                  <span class="ms-auto badge bg-info">Freeze Risk</span>
                </div>
                <div class="input-group input-group-sm">
                  <span class="input-group-text">Don't ship below</span>
                  <input 
                    type="number" 
                    class="form-control" 
                    v-model.number="coldThreshold"
                    style="max-width: 80px;"
                    @change="updateCriteria"
                  >
                  <span class="input-group-text">{{ tempUnit }}</span>
                </div>
                <small class="text-muted d-block mt-1">
                  Products may freeze, batteries fail, liquids expand
                </small>
              </div>

              <!-- Heat Threshold -->
              <div class="threshold-section">
                <div class="d-flex align-items-center mb-2">
                  <i class="bi bi-thermometer-sun text-danger fs-5 me-2"></i>
                  <span class="fw-medium">Heat Threshold</span>
                  <span class="ms-auto badge bg-danger">Heat Damage</span>
                </div>
                <div class="input-group input-group-sm">
                  <span class="input-group-text">Don't ship above</span>
                  <input 
                    type="number" 
                    class="form-control" 
                    v-model.number="heatThreshold"
                    style="max-width: 80px;"
                    @change="updateCriteria"
                  >
                  <span class="input-group-text">{{ tempUnit }}</span>
                </div>
                <small class="text-muted d-block mt-1">
                  Melting, spoilage, chemical degradation risks
                </small>
              </div>
            </div>
          </div>

          <!-- Optional: Extreme Weather -->
          <div class="card mb-3">
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                <i class="bi bi-cloud-lightning me-2"></i>Weather Delays
              </h6>
              <div class="form-check form-switch mb-0">
                <input 
                  type="checkbox" 
                  class="form-check-input" 
                  id="includeWeather"
                  v-model="includeWeatherAlerts"
                  @change="updateCriteria"
                >
              </div>
            </div>
            <div v-if="includeWeatherAlerts" class="card-body">
              <div class="form-check mb-2">
                <input type="checkbox" class="form-check-input" id="alertSnow" 
                  v-model="alertFlags.snow" @change="updateCriteria">
                <label class="form-check-label small" for="alertSnow">
                  <i class="bi bi-snow2 me-1"></i>Heavy snow ({{ unitGroup === 'metric' ? '>10cm' : '>4"' }})
                </label>
              </div>
              <div class="form-check mb-2">
                <input type="checkbox" class="form-check-input" id="alertWind" 
                  v-model="alertFlags.wind" @change="updateCriteria">
                <label class="form-check-label small" for="alertWind">
                  <i class="bi bi-wind me-1"></i>High winds ({{ unitGroup === 'metric' ? '>60 km/h' : '>40 mph' }})
                </label>
              </div>
              <div class="form-check mb-2">
                <input type="checkbox" class="form-check-input" id="alertIce" 
                  v-model="alertFlags.ice" @change="updateCriteria">
                <label class="form-check-label small" for="alertIce">
                  <i class="bi bi-droplet-fill me-1"></i>Freezing rain/ice
                </label>
              </div>
              <div class="form-check">
                <input type="checkbox" class="form-check-input" id="alertStorm" 
                  v-model="alertFlags.storm" @change="updateCriteria">
                <label class="form-check-label small" for="alertStorm">
                  <i class="bi bi-cloud-lightning-rain me-1"></i>Severe weather/storms (>{{ stormRiskThreshold }}% risk)
                </label>
              </div>
              <div v-if="alertFlags.storm" class="mt-2 ms-4">
                <div class="d-flex align-items-center gap-2">
                  <small class="text-muted">Risk threshold:</small>
                  <input 
                    type="range" 
                    class="form-range flex-grow-1" 
                    v-model.number="stormRiskThreshold"
                    min="10" max="80" step="10"
                    style="max-width: 120px;"
                    @change="updateCriteria"
                  >
                  <span class="badge bg-warning text-dark">{{ stormRiskThreshold }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Run Button -->
          <button 
            class="btn btn-primary w-100"
            :disabled="!canRun || isRunning"
            @click="runExceptionCheck"
          >
            <span v-if="isRunning">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Checking Routes...
            </span>
            <span v-else>
              <i class="bi bi-search me-2"></i>
              Check Shipping Conditions
            </span>
          </button>
        </div>

        <!-- Right Column: Results -->
        <div class="col-lg-8">
          <!-- Decision Summary -->
          <div v-if="hasRun && !isRunning" class="decision-summary mb-4">
            <!-- All Clear -->
            <div v-if="allClear" class="alert alert-success d-flex align-items-center">
              <div class="decision-icon success me-3">
                <i class="bi bi-check-lg"></i>
              </div>
              <div>
                <h5 class="alert-heading mb-1">All Clear to Ship!</h5>
                <p class="mb-0">All {{ locationResults.length }} destination(s) have safe temperatures for the shipping window.</p>
              </div>
            </div>

            <!-- Some Issues -->
            <div v-else-if="someIssues" class="alert alert-warning d-flex align-items-center">
              <div class="decision-icon warning me-3">
                <i class="bi bi-exclamation-lg"></i>
              </div>
              <div>
                <h5 class="alert-heading mb-1">Caution: Temperature Concerns</h5>
                <p class="mb-0">
                  {{ dangerCount }} destination(s) have days that may exceed limits. Review before shipping.
                </p>
              </div>
            </div>

            <!-- Major Problems -->
            <div v-else class="alert alert-danger d-flex align-items-center">
              <div class="decision-icon danger me-3">
                <i class="bi bi-x-lg"></i>
              </div>
              <div>
                <h5 class="alert-heading mb-1">Do Not Ship</h5>
                <p class="mb-0">
                  Critical temperature violations detected. Consider delay or climate-controlled transport.
                </p>
              </div>
            </div>
          </div>

          <!-- Running Progress -->
          <ExceptionResultsSummary 
            v-if="isRunning"
            :results="locationResults"
            :isRunning="true"
            :completedCount="completedCount"
            locationLabel="Stop"
            okLabel="Safe"
            warningLabel="Caution"
            dangerLabel="Don't Ship"
            class="mb-4"
          />

          <!-- View Toggle -->
          <div v-if="hasRun && !isRunning" class="d-flex justify-content-between align-items-center mb-3">
            <div class="btn-group btn-group-sm">
              <button 
                class="btn"
                :class="viewMode === 'route' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="viewMode = 'route'"
              >
                <i class="bi bi-signpost me-1"></i>Route
              </button>
              <button 
                class="btn"
                :class="viewMode === 'map' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="viewMode = 'map'"
              >
                <i class="bi bi-map me-1"></i>Map
              </button>
              <button 
                class="btn"
                :class="viewMode === 'grid' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="viewMode = 'grid'"
              >
                <i class="bi bi-grid-3x3 me-1"></i>Calendar
              </button>
            </div>
          </div>

          <!-- Route View (Default) -->
          <div v-if="hasRun && !isRunning && viewMode === 'route'" class="results-view p-3">
            <div class="route-list">
              <div 
                v-for="(result, index) in locationResults" 
                :key="result.id"
                class="route-stop"
              >
                <!-- Stop connector line -->
                <div v-if="index < locationResults.length - 1" class="route-line"></div>
                
                <!-- Stop marker -->
                <div class="route-marker" :class="'severity-' + result.maxSeverity">
                  <span>{{ index + 1 }}</span>
                </div>

                <!-- Stop card -->
                <div class="route-card flex-grow-1" :class="'border-' + (result.maxSeverity === 'ok' ? 'success' : result.maxSeverity === 'warning' ? 'warning' : 'danger')">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 class="mb-1">{{ result.name || result.location }}</h6>
                      <small v-if="result.name" class="text-muted d-block">{{ result.location }}</small>
                    </div>
                    <span class="badge" :class="{
                      'bg-success': result.maxSeverity === 'ok',
                      'bg-warning text-dark': result.maxSeverity === 'warning',
                      'bg-danger': result.maxSeverity === 'danger'
                    }">
                      {{ result.maxSeverity === 'ok' ? 'Safe' : result.maxSeverity === 'warning' ? 'Caution' : "Don't Ship" }}
                    </span>
                  </div>

                  <!-- Temperature strip -->
                  <div class="temp-strip mt-2">
                    <div 
                      v-for="day in result.days?.slice(0, 5)" 
                      :key="day.datetime"
                      class="temp-day"
                      :class="'severity-' + day.severity"
                      :title="formatDate(day.datetime) + ': ' + formatTemp(day.tempmax)"
                    >
                      <div class="day-label">{{ getDayAbbrev(day.datetime) }}</div>
                      <div class="temp-value">{{ formatTempShort(day.tempmax) }}</div>
                    </div>
                  </div>

                  <!-- Issues -->
                  <div v-if="result.exceptionCount > 0" class="mt-2">
                    <small class="text-muted">
                      <i class="bi bi-exclamation-circle me-1"></i>
                      {{ getIssuesSummary(result) }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Map View -->
          <div v-if="hasRun && !isRunning && viewMode === 'map'" class="results-view">
            <ExceptionMapView
              :results="locationResults"
              :isLoading="isRunning"
              height="450px"
              dangerLabel="Don't Ship"
            />
          </div>

          <!-- Grid View -->
          <div v-if="hasRun && !isRunning && viewMode === 'grid'" class="results-view">
            <ExceptionDateGrid
              :results="locationResults"
              :dates="allDates"
              :unitGroup="unitGroup"
              primaryField="tempmax"
              :showSecondary="true"
              secondaryField="tempmin"
              cornerLabel="Stop"
              okLabel="Safe"
              warningLabel="Caution"
              dangerLabel="Don't Ship"
              maxHeight="400px"
            />
          </div>

          <!-- Empty State -->
          <div v-if="!hasRun" class="empty-state text-center py-5">
            <i class="bi bi-truck fs-1 text-muted mb-3 d-block"></i>
            <h5 class="text-muted">Plan Your Shipments</h5>
            <p class="text-muted mb-4">
              Add your delivery stops, set temperature limits, and check conditions.
            </p>
            <div class="text-center">
              <small class="text-muted">
                <i class="bi bi-lightbulb me-1"></i>
                Tip: Use presets for common product types (food, electronics, etc.)
              </small>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useWeatherExceptionEngine } from '../useWeatherExceptionEngine.js';
import { createCriterion } from '../exceptionCriteriaPresets.js';
import { ExceptionLocationPanel, ExceptionMapView, ExceptionResultsSummary, ExceptionDateGrid } from '../core';

export default {
  name: 'ShippingWeatherTracker',

  components: {
    ExceptionLocationPanel,
    ExceptionMapView,
    ExceptionResultsSummary,
    ExceptionDateGrid
  },

  props: {
    /** Optional override for the API server root URL */
    serverRoot: {
      type: String,
      default: null
    }
  },


  setup(props) {
    // Initialize the weather exception engine
    const engine = useWeatherExceptionEngine({
      storageKey: 'shipping-weather',
      defaultStartPeriod: 'next5days',
      defaultUnitGroup: 'us',
      maxConcurrentRequests: 3,
      serverRoot: props.serverRoot
    });

    // Initialize session on mount
    onMounted(() => {
      engine.initSession();
    });

    return {
      ...engine
    };
  },

  data() {
    return {
      // View state
      viewMode: 'route',
      activePreset: null,

      // Temperature thresholds (in current unit group)
      coldThreshold: 32,   // Default: freezing in °F
      heatThreshold: 95,   // Default: hot in °F

      // Weather delay flags
      includeWeatherAlerts: false,
      alertFlags: {
        snow: true,
        wind: true,
        ice: true,
        storm: true
      },
      stormRiskThreshold: 30,  // Severe risk threshold (0-100),

      // Presets for different cargo types
      presets: [
        {
          id: 'general',
          name: 'General Freight',
          icon: 'bi bi-box-seam',
          cold: { metric: -5, us: 23 },
          heat: { metric: 40, us: 104 }
        },
        {
          id: 'food',
          name: 'Food & Beverage',
          icon: 'bi bi-basket',
          cold: { metric: 2, us: 35 },
          heat: { metric: 30, us: 86 }
        },
        {
          id: 'pharma',
          name: 'Pharmaceuticals',
          icon: 'bi bi-capsule',
          cold: { metric: 2, us: 35 },
          heat: { metric: 25, us: 77 }
        },
        {
          id: 'electronics',
          name: 'Electronics',
          icon: 'bi bi-cpu',
          cold: { metric: -10, us: 14 },
          heat: { metric: 35, us: 95 }
        },
        {
          id: 'chemicals',
          name: 'Chemicals/Hazmat',
          icon: 'bi bi-radioactive',
          cold: { metric: 5, us: 41 },
          heat: { metric: 30, us: 86 }
        }
      ]
    };
  },

  computed: {
    tempUnit() {
      return this.unitGroup === 'us' ? '°F' : '°C';
    },

    allClear() {
      return this.locationResults.every(r => r.maxSeverity === 'ok');
    },

    someIssues() {
      return this.dangerCount > 0 && this.dangerCount < this.locationResults.length;
    },

    dangerCount() {
      return this.locationResults.filter(r => r.maxSeverity === 'danger').length;
    }
  },

  watch: {
    unitGroup(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.convertThresholds(oldVal, newVal);
      }
    }
  },

  mounted() {
    this.initializeThresholds();
    this.updateCriteria();
  },

  methods: {
    initializeThresholds() {
      if (this.unitGroup === 'metric') {
        this.coldThreshold = 0;
        this.heatThreshold = 35;
      } else {
        this.coldThreshold = 32;
        this.heatThreshold = 95;
      }
    },

    convertThresholds(fromUnit, toUnit) {
      const convertTemp = (val) => {
        if (fromUnit === 'metric' && toUnit === 'us') {
          return Math.round(val * 9/5 + 32);
        } else if (fromUnit === 'us' && toUnit === 'metric') {
          return Math.round((val - 32) * 5/9);
        }
        return val;
      };

      this.coldThreshold = convertTemp(this.coldThreshold);
      this.heatThreshold = convertTemp(this.heatThreshold);

      this.updateCriteria();
    },

    applyPreset(preset) {
      this.activePreset = preset.id;
      this.coldThreshold = preset.cold[this.unitGroup];
      this.heatThreshold = preset.heat[this.unitGroup];
      this.updateCriteria();
    },

    updateCriteria() {
      const criteria = [];

      // Too cold (DANGER - don't ship)
      criteria.push(createCriterion(
        'tempmin', 'lt', this.coldThreshold, 'days',
        'Too Cold',
        { color: 'danger' }
      ));

      // Too hot (DANGER - don't ship)
      criteria.push(createCriterion(
        'tempmax', 'gt', this.heatThreshold, 'days',
        'Too Hot',
        { color: 'danger' }
      ));

      // Optional weather alerts
      if (this.includeWeatherAlerts) {
        // Heavy snow
        if (this.alertFlags.snow) {
          const snowThreshold = this.unitGroup === 'metric' ? 10 : 4;
          criteria.push(createCriterion(
            'snow', 'gt', snowThreshold, 'days',
            'Heavy Snow',
            { color: 'warning' }
          ));
        }

        // High winds
        if (this.alertFlags.wind) {
          const windThreshold = this.unitGroup === 'metric' ? 60 : 40;
          criteria.push(createCriterion(
            'windgust', 'gt', windThreshold, 'days',
            'High Winds',
            { color: 'warning' }
          ));
        }

        // Freezing rain (preciptype contains freezing rain and temp near freezing)
        if (this.alertFlags.ice) {
          // Use a combination: precip probability high + temp around freezing
          const iceTemp = this.unitGroup === 'metric' ? 2 : 35;
          criteria.push(createCriterion(
            'tempmin', 'lt', iceTemp, 'days',
            'Ice Risk',
            { 
              color: 'warning',
              additionalField: 'precipprob',
              additionalOperator: 'gt',
              additionalValue: 50
            }
          ));
        }

        // Severe weather/storm risk
        if (this.alertFlags.storm) {
          criteria.push(createCriterion(
            'severerisk', 'gt', this.stormRiskThreshold, 'days',
            'Severe Weather',
            { color: 'danger' }
          ));
        }
      }

      this.setCriteria(criteria, 'OR');
    },

    getIssuesSummary(result) {
      if (!result.days) return '';
      
      const coldDays = result.days.filter(d => d.tempmin < this.coldThreshold).length;
      const hotDays = result.days.filter(d => d.tempmax > this.heatThreshold).length;
      
      const parts = [];
      if (coldDays > 0) parts.push(`${coldDays} day(s) too cold`);
      if (hotDays > 0) parts.push(`${hotDays} day(s) too hot`);
      
      return parts.join(', ') || `${result.exceptionCount} day(s) with issues`;
    },

    formatDate(datetime) {
      if (!datetime) return '';
      const d = new Date(datetime + 'T12:00:00');
      return d.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    },

    formatTemp(temp) {
      if (temp == null) return '--';
      return Math.round(temp) + this.tempUnit;
    },

    formatTempShort(temp) {
      if (temp == null) return '--';
      return Math.round(temp) + '°';
    },

    getDayAbbrev(datetime) {
      if (!datetime) return '';
      const d = new Date(datetime + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0);
    }
  }
};
</script>

<style scoped>
.shipping-weather-tracker {
  padding: 1rem;
}

.tracker-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #dee2e6;
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0d6efd 0%, #6610f2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.preset-bar {
  border: 1px solid #dee2e6;
}

.threshold-section {
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
}

/* Decision summary icons */
.decision-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.decision-icon.success {
  background: #198754;
  color: white;
}

.decision-icon.warning {
  background: #ffc107;
  color: #000;
}

.decision-icon.danger {
  background: #dc3545;
  color: white;
}

/* Route view styles */
.results-view {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}

.route-list {
  position: relative;
}

.route-stop {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  position: relative;
  margin-bottom: 1rem;
}

.route-stop:last-child {
  margin-bottom: 0;
}

.route-line {
  position: absolute;
  left: 17px;
  top: 38px;
  width: 2px;
  height: calc(100% - 10px);
  background: #dee2e6;
  z-index: 0;
}

.route-marker {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  flex-shrink: 0;
  z-index: 1;
  font-size: 0.875rem;
}

.route-marker.severity-ok {
  background: #198754;
}

.route-marker.severity-warning {
  background: #ffc107;
  color: #000;
}

.route-marker.severity-danger {
  background: #dc3545;
}

.route-card {
  background: #fff;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.2s;
}

.route-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.route-card.border-success { border-color: #198754; }
.route-card.border-warning { border-color: #ffc107; }
.route-card.border-danger { border-color: #dc3545; }

/* Temperature strip */
.temp-strip {
  display: flex;
  gap: 4px;
}

.temp-day {
  flex: 1;
  text-align: center;
  padding: 4px;
  border-radius: 4px;
  min-width: 40px;
}

.temp-day.severity-ok {
  background: #d1e7dd;
  color: #0f5132;
}

.temp-day.severity-warning {
  background: #fff3cd;
  color: #664d03;
}

.temp-day.severity-danger {
  background: #f8d7da;
  color: #842029;
}

.temp-day .day-label {
  font-size: 0.625rem;
  text-transform: uppercase;
  opacity: 0.7;
}

.temp-day .temp-value {
  font-size: 0.875rem;
  font-weight: 600;
}

.empty-state {
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px dashed #dee2e6;
}
</style>
